import { useEffect } from "react";
import { getPosition, locationErrorMessage } from "../utils/geolocation";
import { showToast } from "../utils/toast";

// Browsers only ever show ONE native permission dialog at a time — a second
// request fired in the same tick is queued or silently auto-dismissed, and
// Chrome hard-blocks geolocation after three dismissals. FcmBootstrap and
// LocationBootstrap mount together in App.js, so we hold the location request
// until the notification prompt has been answered. If the user just ignores
// that dialog we give up waiting and ask anyway.
const NOTIFICATION_WAIT_MS = 30_000;
const POLL_MS = 400;

function notificationPending() {
  return typeof Notification !== "undefined" && Notification.permission === "default";
}

async function waitForNotificationPrompt(state) {
  if (!notificationPending()) return;
  const deadline = Date.now() + NOTIFICATION_WAIT_MS;
  while (notificationPending() && Date.now() < deadline) {
    if (state.cancelled) return;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
}

// Ask for geolocation up-front at app launch (mirrors the FCM permission
// flow) instead of waiting for the first punch/geofence check. Browsers have
// no "always allow" API we can set programmatically — the closest is
// prompting once so the browser persists the choice for this origin on every
// later visit. Geofenced login and attendance punches need coordinates, so a
// denied permission gets a visible warning rather than a silent failure later.
export default function useLocationPermission() {
  useEffect(() => {
    const state = { cancelled: false };

    (async () => {
      await waitForNotificationPrompt(state);
      if (state.cancelled) return;

      // Coarse + a generous cache: this is only warming the permission and the
      // position cache, so there's no reason to wake the GPS at launch.
      const res = await getPosition({ highAccuracy: false, timeout: 15000, maximumAge: 300000 });
      if (state.cancelled || res.ok) return;

      // Only nag about things the user can actually fix. A timeout or a missing
      // fix at launch is not worth a toast — the geofence check and the punch
      // flow both retry and report for themselves.
      if (res.reason === "denied") {
        showToast("warning", locationErrorMessage(res.reason));
      } else if (res.reason === "insecure") {
        // eslint-disable-next-line no-console
        console.warn("[Location] Geolocation requires HTTPS or localhost.");
      }
    })();

    return () => {
      state.cancelled = true;
    };
  }, []);
}
